import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import api from '../services/api';
import type { WhatIfOverrides, WhatIfResponse } from '../types/calculations';
import { useActiveMarket } from '../store/useMarketStore';

/** Generic debounce hook — returns value after delay ms of inactivity. */
export function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

interface WhatIfPayload {
  overrides: WhatIfOverrides;
  hold_period_years: number;
}

// Phase 205: ScenarioControlBar toggle re-fetch payload
interface ScenarioTogglePayload {
  mode: 'off-market' | 'on-market';
  hold_period_years: number;
  tax_scenario?: string;
  financing_mode?: string;
}

/** Mutation hook for what-if scenario recalculation.
 *  POSTs overrides to the underwrite endpoint and returns recalculated result.
 */
export function useWhatIfScenario(propertyId: string | undefined) {
  const activeMarket = useActiveMarket();
  return useMutation({
    mutationFn: async (payload: WhatIfPayload): Promise<WhatIfResponse> => {
      const { data } = await api.post<WhatIfResponse>(
        `/v2/properties/${propertyId}/underwrite`,
        {
          mode: 'off-market',
          hold_period_years: payload.hold_period_years,
          market_id: activeMarket.market_id,
          overrides: payload.overrides,
        }
      );
      return data;
    },
  });
}

/**
 * Phase 205: Mutation hook for ScenarioControlBar toggle changes.
 * Re-fetches with tax_scenario, financing_mode, and mode params.
 * Returns full PropertyAnalysisResult (not WhatIfResponse).
 */
export function useScenarioToggle(propertyId: string | undefined) {
  const activeMarket = useActiveMarket();
  return useMutation({
    mutationFn: async (payload: ScenarioTogglePayload) => {
      const { data } = await api.post(
        `/v2/properties/${propertyId}/underwrite`,
        {
          mode: payload.mode,
          hold_period_years: payload.hold_period_years,
          market_id: activeMarket.market_id,
          tax_scenario: payload.tax_scenario,
          financing_mode: payload.financing_mode,
        }
      );
      // Response may be wrapped in full_analysis or direct
      return data.full_analysis || data;
    },
  });
}
